import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  Textarea,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconSettings } from '@/components/icons'
import { InlineLoader, PageShell, PageToolbar } from '@/components/ui'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError } from '@/lib/api'
import {
  canAccessOperatorQuickSettings,
  defaultPermissionsForRole,
  loadMyPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import { ProjectTemplatesPanel } from '@/pages/settings/ProjectTemplatesPanel'
import type { ChannelRow, MyPermissionsResponse, ProjectDetailResponse, ProjectRow } from '@/types/api'

type ChannelDraft = {
  telegramWelcome: string
  telegramTicketCreated: string
  widgetWelcome: string
}

function channelLabel(channel: ChannelRow) {
  return channel.name || channel.id
}

function TelegramChannelSettings({
  channel,
  draft,
  onChange,
  days,
  busy,
  onDaysChange,
  onResync,
}: {
  channel: ChannelRow
  draft: ChannelDraft
  onChange: (patch: Partial<ChannelDraft>) => void
  days: number
  busy: boolean
  onDaysChange: (days: number) => void
  onResync: () => void
}) {
  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Text fw={600}>{channelLabel(channel)}</Text>
        <Textarea
          label="Приветственное сообщение"
          description="Отправляется пользователю при первом открытии Telegram-бота."
          value={draft.telegramWelcome}
          onChange={(event) => onChange({ telegramWelcome: event.currentTarget.value })}
          minRows={3}
          maxLength={1000}
          autosize
        />
        <Textarea
          label="Сообщение при создании обращения"
          value={draft.telegramTicketCreated}
          onChange={(event) => onChange({ telegramTicketCreated: event.currentTarget.value })}
          minRows={3}
          maxLength={1000}
          autosize
        />
        <Group align="flex-end" gap="sm">
          <NumberInput
            label="Период проверки вложений, дней"
            value={days}
            onChange={(value) => onDaysChange(Number(value || 1))}
            min={1}
            max={365}
            w={230}
          />
          <Button variant="light" loading={busy} onClick={onResync}>
            Перезапросить вложения
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}

function WidgetChannelSettings({
  channel,
  draft,
  onChange,
}: {
  channel: ChannelRow
  draft: ChannelDraft
  onChange: (patch: Partial<ChannelDraft>) => void
}) {
  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Text fw={600}>{channelLabel(channel)}</Text>
        <Textarea
          label="Сообщение в чате после первого сообщения"
          description="Отправляется посетителю в ленте виджета сразу после первого обращения."
          value={draft.widgetWelcome}
          onChange={(event) => onChange({ widgetWelcome: event.currentTarget.value })}
          minRows={3}
          autosize
        />
      </Stack>
    </Paper>
  )
}

export function OperatorQuickSettingsPage() {
  const { user } = useAuth()
  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  useEffect(() => {
    void loadMyPermissions(user?.role).then(setPerm)
  }, [user?.role])
  const effectivePerm = perm ?? defaultPermissionsForRole(user?.role)
  const canPage = canAccessOperatorQuickSettings(effectivePerm)

  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [projectId, setProjectId] = useState('')
  const [subTab, setSubTab] = useState<'messages' | 'templates'>('messages')
  const [loadingList, setLoadingList] = useState(true)
  const [loadingProject, setLoadingProject] = useState(false)
  const [savingProject, setSavingProject] = useState(false)
  const [savingWorkspace, setSavingWorkspace] = useState(false)
  const [requireShift, setRequireShift] = useState(false)
  const [sendAssignment, setSendAssignment] = useState(false)
  const [assignmentText, setAssignmentText] = useState('')
  const [channels, setChannels] = useState<ChannelRow[]>([])
  const [drafts, setDrafts] = useState<Record<string, ChannelDraft>>({})
  const [resyncDays, setResyncDays] = useState<Record<string, number>>({})
  const [resyncBusy, setResyncBusy] = useState<Record<string, boolean>>({})

  const loadProjects = useCallback(async () => {
    setLoadingList(true)
    try {
      const [projectList, workspace] = await Promise.all([api.projects(), api.workspaceMe()])
      const sorted = [...projectList].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
      setProjects(sorted)
      setRequireShift(workspace.require_shift_started_to_work === true)
      setProjectId((current) => (sorted.some((project) => project.id === current) ? current : ''))
    } catch {
      setProjects([])
      showNotification({ title: 'Ошибка', message: 'Не удалось загрузить быстрые настройки', color: 'red' })
    } finally {
      setLoadingList(false)
    }
  }, [])

  useEffect(() => {
    void loadProjects()
  }, [loadProjects])

  const applyProject = useCallback((project: ProjectDetailResponse) => {
    setSendAssignment(project.send_assignment_message === true)
    setAssignmentText(project.assignment_message_text ?? '')
  }, [])

  const loadProject = useCallback(async (id: string) => {
    if (!id) return
    setLoadingProject(true)
    try {
      const [project, channelList] = await Promise.all([api.project(id), api.channelsList(id)])
      applyProject(project)
      setChannels(channelList)
      setDrafts(
        Object.fromEntries(
          channelList.map((channel) => {
            const config = channel.config && typeof channel.config === 'object' ? channel.config : {}
            return [
              channel.id,
              {
                telegramWelcome: channel.telegram_welcome_message ?? '',
                telegramTicketCreated: channel.telegram_ticket_created_message ?? '',
                widgetWelcome: String(config.widget_welcome_message ?? ''),
              },
            ]
          }),
        ),
      )
      setResyncDays((current) =>
        Object.fromEntries(channelList.map((channel) => [channel.id, current[channel.id] ?? 7])),
      )
    } catch {
      setChannels([])
      setDrafts({})
      showNotification({ title: 'Ошибка', message: 'Не удалось загрузить настройки проекта', color: 'red' })
    } finally {
      setLoadingProject(false)
    }
  }, [applyProject])

  useEffect(() => {
    if (projectId) void loadProject(projectId)
  }, [projectId, loadProject])

  const projectOptions = useMemo(
    () => projects.map((project) => ({ value: project.id, label: project.name || project.slug || project.id })),
    [projects],
  )
  const telegramChannels = useMemo(
    () => channels.filter((channel) => String(channel.channel_type) === 'telegram'),
    [channels],
  )
  const widgetChannels = useMemo(
    () => channels.filter((channel) => ['widget', 'webchat'].includes(String(channel.channel_type))),
    [channels],
  )

  const updateDraft = (channelId: string, patch: Partial<ChannelDraft>) => {
    setDrafts((current) => ({
      ...current,
      [channelId]: { ...current[channelId], ...patch },
    }))
  }

  const saveWorkspace = async () => {
    setSavingWorkspace(true)
    try {
      const workspace = await api.workspaceQuickSettings({ require_shift_started_to_work: requireShift })
      setRequireShift(workspace.require_shift_started_to_work === true)
      showNotification({ title: 'Сохранено', message: 'Настройка смены обновлена', color: 'green' })
    } catch (error) {
      showNotification({
        title: 'Ошибка',
        message: error instanceof ApiError ? error.message : 'Не удалось сохранить настройку смены',
        color: 'red',
      })
    } finally {
      setSavingWorkspace(false)
    }
  }

  const saveProjectMessages = async () => {
    if (!projectId) return
    if (sendAssignment && !assignmentText.trim()) {
      showNotification({
        title: 'Сообщение о загрузке',
        message: 'Заполните текст сообщения или отключите его отправку',
        color: 'yellow',
      })
      return
    }

    setSavingProject(true)
    try {
      const projectRequest = api.projectQuickSettings(projectId, {
        send_assignment_message: sendAssignment,
        assignment_message_text: assignmentText.trim() || null,
      })
      const channelRequests = channels.flatMap((channel) => {
        const draft = drafts[channel.id]
        if (!draft) return []
        if (String(channel.channel_type) === 'telegram') {
          return [api.channelUpdate(channel.id, {
            telegram_welcome_message: draft.telegramWelcome.trim() || null,
            telegram_ticket_created_message: draft.telegramTicketCreated.trim() || null,
          })]
        }
        if (['widget', 'webchat'].includes(String(channel.channel_type))) {
          return [api.channelUpdate(channel.id, {
            config: { widget_welcome_message: draft.widgetWelcome.trim() || null },
          })]
        }
        return []
      })
      const [project] = await Promise.all([projectRequest, ...channelRequests])
      applyProject(project)
      showNotification({ title: 'Сохранено', message: 'Сообщения проекта обновлены', color: 'green' })
      await loadProject(projectId)
    } catch (error) {
      showNotification({
        title: 'Ошибка',
        message: error instanceof ApiError ? error.message : 'Не удалось сохранить сообщения проекта',
        color: 'red',
      })
    } finally {
      setSavingProject(false)
    }
  }

  const runResync = async (channel: ChannelRow) => {
    const days = resyncDays[channel.id] ?? 7
    setResyncBusy((current) => ({ ...current, [channel.id]: true }))
    try {
      const result = await api.channelResyncTelegramAttachments(channel.id, days)
      showNotification({
        title: 'Проверка вложений завершена',
        message: `Проверено: ${result.attachments_checked ?? 0}, перезагружено: ${result.redownloaded ?? 0}, проблем: ${result.missing ?? 0}`,
        color: 'green',
      })
    } catch (error) {
      showNotification({
        title: 'Ошибка',
        message: error instanceof ApiError ? error.message : 'Не удалось перезапросить вложения',
        color: 'red',
      })
    } finally {
      setResyncBusy((current) => ({ ...current, [channel.id]: false }))
    }
  }

  if (!canPage) {
    return (
      <PageShell>
        <Alert color="gray" variant="light" title="Нет доступа">
          Недостаточно прав для раздела «Быстрые настройки».
        </Alert>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <Stack gap="lg" w="100%" miw={0}>
        <PageToolbar
          leading={
            <Stack gap={0}>
              <Group gap="xs" wrap="nowrap">
                <IconSettings size={20} stroke={1.6} />
                <Text fw={700} size="lg">Быстрые настройки</Text>
              </Group>
              <Text size="sm" c="dimmed">Шаблоны и сообщения каналов по проектам.</Text>
            </Stack>
          }
        />

        {loadingList ? (
          <InlineLoader label="Загрузка настроек…" />
        ) : (
          <>
            <Card withBorder padding="md" radius="md">
              <Stack gap="md">
                <div>
                  <Text fw={600}>Работа со сменами</Text>
                  <Text size="sm" c="dimmed">Общая настройка рабочего пространства — применяется ко всем проектам.</Text>
                </div>
                <Switch
                  label="Запрет решения обращений пока не начата смена"
                  checked={requireShift}
                  onChange={(event) => setRequireShift(event.currentTarget.checked)}
                />
                <Group justify="flex-end">
                  <Button variant="default" loading={savingWorkspace} onClick={() => void saveWorkspace()}>
                    Сохранить настройку смены
                  </Button>
                </Group>
              </Stack>
            </Card>

            {projectOptions.length === 0 ? (
              <Alert color="yellow" title="Нет проектов">В рабочем пространстве пока нет проектов.</Alert>
            ) : (
              <Card withBorder padding="md" radius="md">
                <Select
                  label="Проект"
                  description="Выберите проект, чтобы изменить его шаблоны и сообщения каналов."
                  data={projectOptions}
                  value={projectId}
                  onChange={(value) => setProjectId(value || '')}
                  placeholder="Выберите проект"
                  searchable
                  clearable
                  checkIconPosition="right"
                />
              </Card>
            )}
          </>
        )}

        {projectId ? (
          <Tabs key={projectId} value={subTab} onChange={(value) => value && setSubTab(value as 'messages' | 'templates')}>
            <Tabs.List>
              <Tabs.Tab value="messages">Сообщения</Tabs.Tab>
              <Tabs.Tab value="templates">Шаблоны сообщений</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="messages" pt="md">
              {loadingProject ? (
                <InlineLoader label="Загрузка настроек проекта…" />
              ) : (
                <Stack gap="lg">
                  <Card withBorder padding="md" radius="md">
                    <Stack gap="sm">
                      <Text fw={600}>Сообщение о загрузке</Text>
                      <Switch
                        label="Отправлять сообщение о загрузке"
                        checked={sendAssignment}
                        onChange={(event) => setSendAssignment(event.currentTarget.checked)}
                      />
                      <Textarea
                        label="Текст сообщения"
                        value={assignmentText}
                        onChange={(event) => setAssignmentText(event.currentTarget.value)}
                        disabled={!sendAssignment}
                        minRows={4}
                        autosize
                      />
                    </Stack>
                  </Card>

                  <Stack gap="sm">
                    <div>
                      <Text fw={600}>Telegram</Text>
                      <Text size="sm" c="dimmed">Настройки сохраняются отдельно для каждого канала проекта.</Text>
                    </div>
                    {telegramChannels.length ? telegramChannels.map((channel) => (
                      <TelegramChannelSettings
                        key={channel.id}
                        channel={channel}
                        draft={drafts[channel.id]}
                        onChange={(patch) => updateDraft(channel.id, patch)}
                        days={resyncDays[channel.id] ?? 7}
                        busy={resyncBusy[channel.id] === true}
                        onDaysChange={(days) => setResyncDays((current) => ({ ...current, [channel.id]: days }))}
                        onResync={() => void runResync(channel)}
                      />
                    )) : (
                      <Alert color="gray" variant="light">В проекте нет каналов Telegram.</Alert>
                    )}
                  </Stack>

                  <Stack gap="sm">
                    <div>
                      <Text fw={600}>Виджет</Text>
                      <Text size="sm" c="dimmed">Сообщение после первого обращения настраивается для каждого виджета.</Text>
                    </div>
                    {widgetChannels.length ? widgetChannels.map((channel) => (
                      <WidgetChannelSettings
                        key={channel.id}
                        channel={channel}
                        draft={drafts[channel.id]}
                        onChange={(patch) => updateDraft(channel.id, patch)}
                      />
                    )) : (
                      <Alert color="gray" variant="light">В проекте нет каналов виджета.</Alert>
                    )}
                  </Stack>

                  <Group justify="flex-end">
                    <Button loading={savingProject} onClick={() => void saveProjectMessages()}>
                      Сохранить сообщения проекта
                    </Button>
                  </Group>
                </Stack>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="templates" pt="md">
              <ProjectTemplatesPanel projectId={projectId} canEdit={canPage} />
            </Tabs.Panel>
          </Tabs>
        ) : null}
      </Stack>
    </PageShell>
  )
}
